import React, { useState } from 'react';
import styled from 'styled-components';
import { FiSearch, FiRefreshCw } from 'react-icons/fi';
import {
  colors,
  typography,
  spacing,
  borderRadius,
  shadows,
} from '../styles/designTokens';

interface CustomerSearchBarMobileProps {
  searchQuery: string;
  limit: number;
  total: number;
  loading?: boolean;
  onSearch: (query: string) => void;
  onLimitChange: (limit: number) => void;
  onRefresh: () => void;
}

const MobileSearchContainer = styled.div`
  background-color: ${colors.semantic.background.primary};
  border-radius: ${borderRadius.xl};
  padding: ${spacing[4]};
  box-shadow: ${shadows.sm};
  border: 1px solid ${colors.semantic.border.primary};
`;

const MobileStatsArea = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: linear-gradient(
    135deg,
    ${colors.secondary[50]} 0%,
    ${colors.secondary[200]} 100%
  );
  border-radius: ${borderRadius.md};
  border: 1px solid ${colors.semantic.border.primary};
  margin-bottom: 10px;
`;

const MobileStatsIcon = styled.div`
  width: 16px;
  height: 16px;
  background: linear-gradient(
    135deg,
    ${colors.primary[500]},
    ${colors.primary[700]}
  );
  border-radius: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  color: ${colors.semantic.text.inverse};
`;

const MobileStatsText = styled.div`
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.semantic.text.secondary};

  .count {
    color: ${colors.primary[700]};
    font-weight: ${typography.fontWeight.bold};
  }
`;

const MobileSearchInputArea = styled.div`
  display: flex;
  gap: ${spacing[2]};
  margin-bottom: ${spacing[3]};
`;

const MobileSearchInputContainer = styled.div`
  position: relative;
  flex: 1;
`;

const MobileSearchInput = styled.input`
  width: 100%;
  padding: ${spacing[3]} ${spacing[4]} ${spacing[3]} 40px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${borderRadius.lg};
  font-size: ${typography.fontSize.sm};
  background-color: ${colors.semantic.background.primary};
  box-sizing: border-box;
  font-family: ${typography.fontFamily.sans};
  color: ${colors.semantic.text.primary};

  &:focus {
    outline: none;
    border-color: ${colors.primary[500]};
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: ${colors.semantic.text.disabled};
  }
`;

const MobileSearchIcon = styled.div`
  position: absolute;
  left: ${spacing[3]};
  top: 50%;
  transform: translateY(-50%);
  color: ${colors.semantic.text.tertiary};
  font-size: ${typography.fontSize.base};
`;

const MobileFilterArea = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
`;

const MobilePageSizeArea = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  flex: 1;
`;

const MobileButtonGroup = styled.div`
  display: flex;
  gap: ${spacing[2]};
`;

const MobileSearchButton = styled.button`
  padding: 10px;
  background-color: ${colors.primary[500]};
  color: ${colors.semantic.text.inverse};
  border: none;
  border-radius: ${borderRadius.lg};
  cursor: pointer;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  height: 40px;

  &:hover {
    background-color: ${colors.primary[600]};
  }

  &:active {
    transform: scale(0.98);
  }
`;

const MobileResetButton = styled.button`
  padding: 10px;
  background-color: ${colors.neutral[100]};
  color: ${colors.semantic.text.secondary};
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${borderRadius.lg};
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  height: 40px;

  &:hover {
    background-color: ${colors.neutral[200]};
  }

  &:active {
    transform: scale(0.98);
  }
`;

const MobilePageSizeLabel = styled.span`
  font-size: ${typography.fontSize.sm};
  color: ${colors.semantic.text.secondary};
  font-weight: ${typography.fontWeight.medium};
`;

const MobilePageSizeSelect = styled.select`
  padding: ${spacing[2]} ${spacing[3]};
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.sm};
  background-color: ${colors.semantic.background.primary};
  cursor: pointer;
  font-family: ${typography.fontFamily.sans};
  color: ${colors.semantic.text.primary};

  &:focus {
    outline: none;
    border-color: ${colors.primary[500]};
  }
`;

const CustomerSearchBarMobile: React.FC<CustomerSearchBarMobileProps> = ({
  searchQuery,
  limit,
  total,
  loading = false,
  onSearch,
  onLimitChange,
  onRefresh,
}) => {
  const [inputValue, setInputValue] = useState(searchQuery);

  const handleSearch = () => {
    onSearch(inputValue.trim());
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleLimitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onLimitChange(Number(e.target.value));
  };

  return (
    <MobileSearchContainer>
      <MobileStatsArea>
        <MobileStatsIcon>👥</MobileStatsIcon>
        <MobileStatsText>
          총 <span className="count">{loading ? '...' : total.toLocaleString()}</span>명
        </MobileStatsText>
      </MobileStatsArea>

      <MobileSearchInputArea>
        <MobileSearchInputContainer>
          <MobileSearchIcon>
            <FiSearch />
          </MobileSearchIcon>
          <MobileSearchInput
            type="text"
            placeholder="이름, ID, 이메일로 검색..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
          />
        </MobileSearchInputContainer>
      </MobileSearchInputArea>

      <MobileFilterArea>
        <MobilePageSizeArea>
          <MobilePageSizeLabel>표시 개수:</MobilePageSizeLabel>
          <MobilePageSizeSelect value={limit} onChange={handleLimitChange}>
            <option value={15}>15개</option>
            <option value={30}>30개</option>
            <option value={50}>50개</option>
            <option value={100}>100개</option>
          </MobilePageSizeSelect>
        </MobilePageSizeArea>

        <MobileButtonGroup>
          <MobileSearchButton onClick={handleSearch}>
            <FiSearch size={16} />
          </MobileSearchButton>
          <MobileResetButton onClick={onRefresh}>
            <FiRefreshCw size={16} />
          </MobileResetButton>
        </MobileButtonGroup>
      </MobileFilterArea>
    </MobileSearchContainer>
  );
};

export default CustomerSearchBarMobile;
